// The Coach panel — a DOM overlay (rem-sized, i18n-friendly) FLOATING over
// the table at the desktop `coachSafeArea` dock position, the same way
// ActionBar/LessonCard float over the canvas — the table itself fills the
// full viewport and is not shrunk to make room for it. It renders the
// live hand-shape summary, a Hint button that reveals the suggested discard +
// reason, and — automatically during the claim window — callouts for each
// available call. All copy comes from `t()`; the Coach analyzer itself stays
// pure (see coach/Coach.ts).
//
// Mobile behaviour (iteration 1): `coachSafeArea` is zero-sized on mobile, so
// the panel hides entirely. The coach is a desktop-first teaching aid here; a
// mobile bottom-sheet is deferred rather than half-built.

import { clear, el } from '@bn-games/ui-shell';
import { t } from '../i18n/I18n.js';
import type { LayoutConfig, Rect } from '../layout/types.js';
import type { CoachAdvice, DiscardAdvice, HandShape } from '../coach/types.js';
import { tileName, translate } from './tileName.js';

const PANEL_STYLE = [
  'position:fixed',
  'box-sizing:border-box',
  'display:flex',
  'flex-direction:column',
  'gap:0.75rem',
  'padding:1.25rem 1rem',
  'background:rgba(6,32,24,0.82)',
  'color:#f4e6c0',
  'z-index:15',
  'overflow-y:auto',
  'font-family:system-ui,-apple-system,sans-serif',
].join(';');

const HEADER_ROW_STYLE = 'display:flex;align-items:center;justify-content:space-between;gap:0.5rem';
const HEADING_STYLE = 'margin:0;font-size:1.15rem;font-weight:700;letter-spacing:0.02em';
const CLOSE_BUTTON_STYLE = [
  'padding:0.15rem 0.5rem',
  'border:none',
  'border-radius:0.35rem',
  'font-size:1.1rem',
  'line-height:1.2',
  'cursor:pointer',
  'background:rgba(255,255,255,0.12)',
  'color:#f4e6c0',
].join(';');
const SUMMARY_STYLE = 'margin:0;font-size:0.95rem;opacity:0.85;line-height:1.4';
const SECTION_STYLE = 'display:flex;flex-direction:column;gap:0.35rem';
const ADVICE_TITLE_STYLE = 'margin:0;font-size:1rem;font-weight:600';
const ADVICE_BODY_STYLE = 'margin:0;font-size:0.9rem;opacity:0.9;line-height:1.4';
const BUTTON_STYLE = [
  'align-self:flex-start',
  'padding:0.55rem 1.1rem',
  'border:none',
  'border-radius:0.5rem',
  'font-size:0.95rem',
  'font-weight:600',
  'cursor:pointer',
  'background:#f0d898',
  'color:#1a1614',
].join(';');

interface LastUpdate {
  readonly advice: CoachAdvice;
  readonly layout: LayoutConfig;
  readonly coachEnabled: boolean;
}

export class CoachPanel {
  private readonly root: HTMLElement;
  private hintTileId: number | null = null;
  private hintRevealed = false;
  private last: LastUpdate | null = null;

  /** `onDismiss` fires when the player closes the panel from its own header —
   *  the caller is expected to turn `coachEnabled` off (see `GameScene`), so
   *  this stays consistent with the Settings screen's coach toggle rather
   *  than tracking a second, separate "hidden" flag. */
  constructor(private readonly onDismiss: () => void) {
    this.root = el('div', { attrs: { style: `${PANEL_STYLE};display:none` } });
    document.body.appendChild(this.root);
  }

  /** Re-render for the current advice. Hides when the coach is off or on mobile
   *  (zero-width safe area). Cheap enough to call every dirty frame. */
  update(advice: CoachAdvice, layout: LayoutConfig, coachEnabled: boolean): void {
    const area = layout.coachSafeArea;
    if (!coachEnabled || area.width === 0) {
      this.hide();
      return;
    }
    this.last = { advice, layout, coachEnabled };
    this.syncHintState(advice.discardAdvice);
    this.position(area);

    clear(this.root);
    this.root.appendChild(this.header());
    this.root.appendChild(this.summary(advice.handShape));
    if (advice.claimAdvice !== null) {
      this.root.appendChild(this.claimSection(advice.claimAdvice));
    } else if (advice.discardAdvice !== null) {
      this.root.appendChild(this.discardSection(advice.discardAdvice));
    }
    this.root.style.display = 'flex';
  }

  /** Hide the panel but keep its node — the scene re-mounts and calls `update`. */
  clear(): void {
    this.hide();
  }

  dispose(): void {
    this.root.remove();
  }

  private hide(): void {
    this.root.style.display = 'none';
  }

  private position(area: Rect): void {
    this.root.style.left = `${area.x}px`;
    this.root.style.top = `${area.y}px`;
    this.root.style.width = `${area.width}px`;
    this.root.style.height = `${area.height}px`;
  }

  /** Reset the revealed-hint flag whenever the suggested tile changes (new turn). */
  private syncHintState(discard: DiscardAdvice | null): void {
    const id = discard?.tile.id ?? null;
    if (id !== this.hintTileId) {
      this.hintTileId = id;
      this.hintRevealed = false;
    }
  }

  private header(): HTMLElement {
    return el('div', { attrs: { style: HEADER_ROW_STYLE } }, [
      el('h2', { text: t('coach.title'), attrs: { style: HEADING_STYLE } }),
      el('button', {
        text: '×',
        attrs: { type: 'button', style: CLOSE_BUTTON_STYLE, 'aria-label': t('coach.hide') },
        on: { click: () => this.onDismiss() },
      }),
    ]);
  }

  private summary(shape: HandShape): HTMLElement {
    const parts = [
      t('coach.setsCount', { n: shape.completeSets.length }),
      shape.pair !== null ? t('coach.pairOne') : t('coach.pairNone'),
      t('coach.floatersCount', { n: shape.floaters.length }),
    ];
    return el('p', { text: parts.join(' · '), attrs: { style: SUMMARY_STYLE } });
  }

  private discardSection(advice: DiscardAdvice): HTMLElement {
    if (!this.hintRevealed) {
      return el('button', {
        text: t('coach.hint'),
        attrs: { type: 'button', style: BUTTON_STYLE },
        on: { click: () => this.revealHint() },
      });
    }
    const name = tileName(advice.tile);
    return el('div', { attrs: { style: SECTION_STYLE } }, [
      el('p', { text: t('coach.discardIntro', { tile: name }), attrs: { style: ADVICE_TITLE_STYLE } }),
      el('p', { text: translate(advice.reasonKey, { tile: name }), attrs: { style: ADVICE_BODY_STYLE } }),
    ]);
  }

  private claimSection(advice: CoachAdvice['claimAdvice']): HTMLElement {
    const children = [
      el('p', { text: t('coach.claimTitle'), attrs: { style: ADVICE_TITLE_STYLE } }),
    ];
    for (const entry of advice ?? []) {
      children.push(el('p', { text: translate(entry.reasonKey), attrs: { style: ADVICE_BODY_STYLE } }));
    }
    return el('div', { attrs: { style: SECTION_STYLE } }, children);
  }

  private revealHint(): void {
    this.hintRevealed = true;
    if (this.last !== null) {
      this.update(this.last.advice, this.last.layout, this.last.coachEnabled);
    }
  }
}
